/**
 * Real `SiteScanAPI` implementation backed by the HTTP client (ADR-0184).
 *
 * Wire mapping:
 *   - trigger:    POST /v1/sites/:site_id/scan
 *                 202 + `{ scan_id, status: 'running' }`
 *                 404 → null (feature flag off or ANTHROPIC_API_KEY unset)
 *                 429 → re-thrown as AuthRateLimitError (server enforces
 *                       3 scans / site / hour)
 *
 *   - getLatest:  GET /v1/sites/:site_id/scan
 *                 200 + `{ scan: <SiteScanRow> | null }`
 *                 404 → null (site unknown under tenancy)
 */

import type { SiteScanAPI, SiteScanRow, SiteScanTriggerResult } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealSiteScanDeps {
  readonly http: HttpClient;
}

interface GetScanEnvelope {
  scan: SiteScanRow | null;
}

export function createRealSiteScanApi(deps: RealSiteScanDeps): SiteScanAPI {
  return {
    async trigger(site_id): Promise<SiteScanTriggerResult | null> {
      try {
        const res = await deps.http.request<SiteScanTriggerResult>({
          method: 'POST',
          path: `/v1/sites/${encodeURIComponent(site_id)}/scan`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return null;
        }
        throw err;
      }
    },

    async getLatest(site_id): Promise<SiteScanRow | null> {
      try {
        const res = await deps.http.request<GetScanEnvelope>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(site_id)}/scan`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        return res.scan;
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') {
          return null;
        }
        throw err;
      }
    },
  };
}
